import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Box, Grid } from '@mui/material';
import {
  useUpdateUserProfileMutation,
  useGetUserProfileQuery,
  UserUpdateInput,
} from '../../../../generated/graphql';
import { formikValidation } from '@/common/models/formikValidation';
import { FormTextField } from '@/common/components/form/FormTextField';
import { useFormMutation } from '@/hooks/useFormMutation';

const UserProfileForm: React.FC = () => {
  const [updateUser, { loading: profileMutationLoading }] =
    useUpdateUserProfileMutation();

  const { data: profileData, refetch: refetchProfile } = useGetUserProfileQuery(
    {
      fetchPolicy: 'cache-first',
    }
  );
  const { email, artistName, firstName, lastName } = profileData?.user || {};

  const { handleSubmit } = useFormMutation({
    mutation: [updateUser, { loading: profileMutationLoading }] as any, // Type assertion for compatibility
    mapValuesToVariables: (values: UserUpdateInput) => ({
      data: {
        firstName: values?.firstName,
        lastName: values?.lastName,
        artistName: values?.artistName,
      },
    }),
    onSuccess: async () => {
      await refetchProfile();
    },
    successMessage: 'Profile updated successfully',
    errorMessage: 'Failed to update profile. Please try again.',
  });

  return (
    <>
      <Formik
        enableReinitialize
        validateOnBlur
        initialValues={
          {
            email: email || '',
            artistName: artistName || '',
            firstName: firstName || '',
            lastName: lastName || '',
          } as UserUpdateInput
        }
        validationSchema={Yup.object().shape({
          ...formikValidation.firstName,
          ...formikValidation.lastName,
          ...formikValidation.artistName,
        })}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isSubmitting, dirty, isValid }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={2} direction="column" mt={1}>
              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="email"
                  aria-label="email"
                  label="Email"
                  type="email"
                  disabled
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="artistName"
                  aria-label="Artist Name"
                  type="artistName"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="firstName"
                  aria-label="first name input"
                  label="First Name"
                  type="firstName"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="lastName"
                  aria-label="last name input"
                  label="Last Name"
                  type="lastName"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 3,
              }}
            >
              <Button
                type="submit"
                variant="contained"
                color="success"
                name="updateProfile"
                aria-label="Update Profile"
                disabled={!(isValid && dirty && !isSubmitting)}
                loading={profileMutationLoading}
              >
                Update Profile
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UserProfileForm;
